import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import {
  getActiveDocument,
  getClientById,
  getClientDocuments,
  getUser,
} from "../service/weexpertService";
import { useQuery } from "react-query";
import { Loader } from "@/components/ProtectedRoute";
import ClientDocumentUpload from "@/components/popups/client-documents.upload.popup";
import { v4 as uuidv4 } from "uuid";
import { DataTablePagination } from "@/components/ui/table-pagination";
import {
  DOSTEP_DO_RYNKU_PRACY,
  NIEPODJECIE,
  PAGINATION_SETTINGS,
  PODJECIE,
  QUESTIONARY,
  ZAKONCZENIE_PRACY,
  ZGLOSZENIE_DO_ZEZWOLENIA,
} from "@/constants";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Skeleton } from "@radix-ui/themes";
import { formatDateString } from "@/styles";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BadgeAlertIcon, BadgeCheckIcon } from "lucide-react";

const getDocumentView = (res) => {
  const parts = [];
  if (res.passport) {
    parts.push(
      <Badge
        variant="secondary"
        className="bg-blue-400 text-white dark:bg-blue-400 mr-2"
      >
        <BadgeCheckIcon className="mr-1" />
        Passport
      </Badge>,
    );
  } else {
    parts.push(
      <Badge variant="destructive" className={"mr-2"}>
        <BadgeAlertIcon className="mr-1" />
        Passport
      </Badge>,
    );
  }
  if (res.visa) {
    parts.push(
      <Badge
        variant="secondary"
        className="bg-blue-400 text-white dark:bg-blue-400 mr-2"
      >
        <BadgeCheckIcon className="mr-1" />
        Visa
      </Badge>,
    );
  } else {
    parts.push(
      <Badge variant="destructive" className="mr-2 bg-red-400">
        <BadgeAlertIcon className="mr-1" />
        Visa
      </Badge>,
    );
  }
  if (res.decyzia) {
    parts.push(
      <Badge
        variant="secondary"
        className="bg-blue-400 text-white dark:bg-blue-400 mr-2"
      >
        <BadgeCheckIcon className="mr-1" />
        Decyzia
      </Badge>,
    );
  } else {
    parts.push(
      <Badge variant="destructive" className="mr-2 bg-red-400">
        <BadgeAlertIcon className="mr-1" />
        Decyzia
      </Badge>,
    );
  }
  if (res.peselUkr) {
    parts.push(
      <Badge
        variant="secondary"
        className="bg-blue-400 text-white dark:bg-blue-400 mr-2"
      >
        <BadgeCheckIcon className="mr-1" />
        Pesel ukr
      </Badge>,
    );
  } else {
    parts.push(
      <Badge variant="destructive" className="mr-2 bg-red-400">
        <BadgeAlertIcon className="mr-1" />
        Pesel ukr
      </Badge>,
    );
  }
  return parts;
};

const getStatusBadgeClasses = (status) => {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-500 text-white hover:bg-green-600';
    case 'APPROVED':
      return 'bg-blue-500 text-white hover:bg-blue-600';
    case 'ARCHIVE':
      return 'bg-gray-500 text-white hover:bg-gray-600';
    case 'EXPIRED':
      return 'bg-orange-500 text-white hover:bg-orange-600';
    case 'DENIED':
      return 'bg-red-500 text-white hover:bg-red-600';
    default:
      return 'bg-neutral-500 text-white hover:bg-neutral-600';
  }
};

export const ClientDocumentsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data: user, isLoading: isUserLoading } = useQuery({
    queryKey: "userMe",
    queryFn: getUser,
    retry: false,
  });
  const {
    data: client,
    isLoading,
    isFetched,
  } = useQuery({
    queryKey: ["client", id],
    queryFn: () => {
      return getClientById(id);
    },
  });

  const { data: documents, isDocumentLoading } = useQuery({
    queryKey: ["client_documents", id],
    queryFn: () => {
      return getClientDocuments(id);
    },
  });

  const { data: activeDocuments, isActiveDocumentLoadiong } = useQuery({
    queryKey: ["active_documents", id],
    queryFn: () => {
      return getActiveDocument(id);
    },
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_SETTINGS.documents);
  const [pagination, setPagination] = useState({
    pageIndex: page,
    pageSize: rowsPerPage,
  });

  const columns = [
    {
      accessorKey: "entityType",
      header: t("document_type"),
      cell: ({ row }) => {
        const type = row.original.entityType;
        let displayType = type;
        switch (type) {
          case "PASSPORT":
            displayType = "Paszport";
            break;
          case "VISA":
            displayType = "Wiza";
            break;
          case QUESTIONARY:
            displayType = "Questionariusz";
            break;
          case "ZEZWOLENIE_TYPY_A":
            displayType = "Zezwolenie typu A";
            break;
          case "OSWIADCZENIE":
            displayType = "Oświadczenie";
            break;
          case "PESEL_UKR":
            displayType = "PESEL UKR";
            break;
          case "LEGITYMACIA":
            displayType = "Legitymacja";
            break;
          case "ZASWIADCZENIE_Z_DZIEKANATY":
            displayType = "Zaświadczenie z dziekanatu";
            break;
          case "KARTA_POBYTU":
            displayType = "Karta pobytu";
            break;
          case "DECYZJA_DO_KARTY_POBYTU":
            displayType = "Decyzja do karty pobytu";
            break;
          case "ZGLOSZENIE_UKR":
            displayType = "Zgłoszenie UKR";
            break;
          case DOSTEP_DO_RYNKU_PRACY:
            displayType = "Dostep do rynku pracy";
            break;
          case ZAKONCZENIE_PRACY:
            displayType = "Zakonczenie pracy";
            break;
          case PODJECIE:
            displayType = "Podjecie";
            break;
          case NIEPODJECIE:
            displayType = "Niepodjecie";
            break;
          case ZGLOSZENIE_DO_ZEZWOLENIA:
            displayType = "Zgloszenie do zezwolenia";
            break;
          default: {
            return type;
          }
        }
        return (
          <Link
            className="capitalize w-full block h-full"
            to={`/client/${client.data.id}/document/${row.original.id}`}
          >
            {displayType}
          </Link>
        );
      },
      key: uuidv4(),
    },
    {
      accessorKey: "status",
      header: t("status"),
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Link
            className="w-full block h-full"
            to={`/client/${client.data.id}/document/${row.original.id}`}
          >
            <Badge className={`${getStatusBadgeClasses(status)} capitalize`}>
              {status.toLowerCase()}
            </Badge>
          </Link>
        );
      },
      key: uuidv4(),
    },
    {
      accessorKey: "createdAt",
      header: t("created_at"),
      cell: ({ row }) => (
        <Link
          className="capitalize w-full block h-full"
          to={`/client/${client.data.id}/document/${row.original.id}`}
        >
          {formatDateString(row.original.createdAt)}
        </Link>
      ),
      key: uuidv4(),
    },
    {
      accessorKey: "validFrom",
      header: t("valid_from"),
      cell: ({ row }) => (
        <Link
          className="capitalize w-full block h-full"
          to={`/client/${client.data.id}/document/${row.original.id}`}
        >
          {row.original.validFrom
            ? formatDateString(row.original.validFrom)
            : t("not_specified")}
        </Link>
      ),
      key: uuidv4(),
    },
    {
      accessorKey: "validTo",
      header: t("valid_to"),
      cell: ({ row }) => (
        <Link
          className="capitalize w-full block h-full"
          to={`/client/${client.data.id}/document/${row.original.id}`}
        >
          {row.original.validTo
            ? formatDateString(row.original.validTo)
            : t("not_specified")}
        </Link>
      ),
      key: uuidv4(),
    },
  ];

  const table = useReactTable({
    data: isDocumentLoading
      ? Array(rowsPerPage).fill({})
      : documents?.data?.documents,
    columns: isDocumentLoading
      ? columns.map((column) => {
          return {
            ...column,
            cell: () => {
              return <Skeleton className="h-4 w-[200px]" />;
            },
          };
        })
      : columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount: documents?.data.total ?? 0,
    state: {
      pagination,
    },
    initialState: {
      pagination,
    },
  });
  if (isLoading) {
    return <Loader />;
  }

  if (isUserLoading) {
    return <Loader />;
  }

  if (
    isDocumentLoading ||
    !documents ||
    !activeDocuments ||
    isActiveDocumentLoadiong ||
    !documents.data ||
    !activeDocuments.data
  ) {
    return <Loader />;
  }

  return (
    <div className="relative bg-grayLightMainBg px-8">
      <div className="sticky left-0 top-16 z-10 w-full bg-grayLightMainBg">
        <div className="flex justify-between py-8 items-center ">
          <div className=" w-5/12  text-4xl ">{t("clients:Documents")}</div>
          <div className="flex justify-end">
            <div>
              <ClientDocumentUpload
                id={client.data.id}
                documents={documents.data.documents}
              />
            </div>
            <Link to={`/admin/client/details/${client.data.id}`}>
              <Button className="min-w-20">{t("button_back")}</Button>
            </Link>
          </div>
        </div>
      </div>
      <div className="w-full pt-8">
        <div className=" text-2xl ">
          {client.data && client.data.firstName + " " + client.data.lastName}
        </div>
        <div className="py-4">
          {getDocumentView(activeDocuments.data)}
          </div>
      </div>
      <div className="rounded-md border p-2">
        {
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={uuidv4()}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead className="pt-3 pb-3" key={uuidv4()}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        className="relative text-sm pt-3 pb-3"
                        key={uuidv4()}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {t("no_results")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        }
      </div>
      <DataTablePagination table={table} />
    </div>
  );
};
